import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { APP_PATHS } from '@core/constants/route.constant';
import { zonedInstant } from '@core/helpers/slot-series.helper';
import {
  Binding,
  BindingState,
  InstallationTelemetry,
  MeasurementPoint,
  Point,
  PointSource,
  Source,
} from '@core/models/installation.model';
import { ApplicationInsightsService } from '@core/services/application-insights.service';
import { TagComponent } from '@custom-components/general/tag/tag.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { InsightSeries } from '../installation-card-set';

const PROVENANCE_ICONS: Record<string, string> = {
  meter: 'speed',
  certified_meter: 'verified',
  calculated: 'calculate',
  modelled: 'model_training',
  vendor_cloud: 'cloud',
};

export interface StripRow {
  key: string;
  provenance: string;
  identity: string;
  deviceLink: unknown[] | null;
  // Null when the period holds no data, undefined while the devices' last readouts are being read.
  lastDataAt: string | null | undefined;
  // Only a measured point carries a state chip: a derived point is unbound by construction.
  warning: BindingState | null;
  sources: PointSource[];
}

// Compared as instants: times carrying different UTC offsets do not sort as text.
export function lastDataAt(points: (Point | null)[]): string | null {
  let latest: string | null = null;
  for (const point of points) {
    for (const slot of point?.slots ?? []) {
      if (
        slot.quality !== 'missing' &&
        (latest === null || Date.parse(slot.time) > Date.parse(latest))
      ) {
        latest = slot.time;
      }
    }
  }
  return latest;
}

function newestInstant(instants: (string | null | undefined)[]): string | null {
  let newest: string | null = null;
  for (const instant of instants) {
    if (instant && (newest === null || Date.parse(instant) > Date.parse(newest))) {
      newest = instant;
    }
  }
  return newest;
}

export function boundDeviceId(point: MeasurementPoint): number | null {
  return point.state === 'active' && point.binding?.source.kind === 'device_channel'
    ? point.binding.source.deviceId
    : null;
}

// A readout is the device's, not the channel's: while a device reports other channels, a point whose
// own blocks stayed empty would still look fresh. So a point only takes a readout made before the
// end of the block that holds its own newest data, which also keeps it inside the period on screen.
function withinNewestBlock(
  readout: string | null,
  series: Point | undefined,
  periodEnd: string,
): string | null {
  const slots = series?.slots ?? [];
  for (let index = slots.length - 1; index >= 0; index--) {
    if (slots[index].quality !== 'missing') {
      const blockEnd = slots[index + 1]?.time ?? periodEnd;
      return readout !== null && Date.parse(readout) < Date.parse(blockEnd) ? readout : null;
    }
  }
  return null;
}

// A slot only says which block holds data, and a day or a month starts long before the newest
// reading in it. When that reading arrived is the last readout of the device behind the point; a
// calculated point takes the newest of the readings of the roles it is calculated from.
export function latestReadingByPoint(
  points: MeasurementPoint[],
  telemetry: Pick<InstallationTelemetry, 'points' | 'periodEnd'> | null,
  readouts: ReadonlyMap<number, string | null>,
): Map<string, string | null> {
  const readings = new Map<string, string | null>();
  if (telemetry === null) {
    return readings;
  }

  const seriesOf = (point: MeasurementPoint): Point | undefined =>
    telemetry.points.find(candidate => candidate.pointId === point.pointOid);
  const measuredReading = (point: MeasurementPoint): string | null => {
    const deviceId = boundDeviceId(point);
    const readout = deviceId === null ? null : (readouts.get(deviceId) ?? null);
    return withinNewestBlock(readout, seriesOf(point), telemetry.periodEnd);
  };

  for (const point of points) {
    if (point.nature === 'derived') {
      const series = seriesOf(point);
      const inputs = series?.sources[0]?.source.inputs ?? [];
      const reading = newestInstant(
        points.filter(input => inputs.includes(input.role)).map(measuredReading),
      );
      readings.set(point.pointOid, withinNewestBlock(reading, series, telemetry.periodEnd));
    } else {
      readings.set(point.pointOid, measuredReading(point));
    }
  }
  return readings;
}

function channelIdentity(source: Source): string {
  return [source.ldn, source.label ?? source.valueName].filter(Boolean).join(' · ');
}

function deviceLink(source: Source | undefined): unknown[] | null {
  if (source?.kind !== 'device_channel' || source.deviceId === null) {
    return null;
  }
  return ['/', APP_PATHS.Devices, String(source.deviceId), 'general'];
}

@Component({
  selector: 'xemex-source-strip',
  standalone: true,
  imports: [MatIconModule, RouterLink, TagComponent, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './source-strip.component.html',
  styleUrl: './source-strip.component.scss',
})
export class SourceStripComponent {
  series = input.required<InsightSeries[]>();
  timezone = input.required<string>();
  latestReadings = input<ReadonlyMap<string, string | null> | undefined>(new Map());
  // Where the state chip of a point that is not bound leads: the place to bind it.
  stateLink = input<unknown[] | null>(null);

  private readonly translate = inject(TranslateService);
  private readonly appInsights = inject(ApplicationInsightsService);

  readonly expanded = signal<ReadonlySet<string>>(new Set());

  private readonly measured = computed(() =>
    this.series().filter(series => series.point.nature === 'measured'),
  );

  // Measured points that read from one device with one provenance read as one line, however many
  // channels they take from it. A point that needs a look keeps its own line: one that is not
  // bound, or that was read from several sources over the period.
  private readonly deviceGroups = computed(() => {
    const groups = new Map<string, { binding: Binding; members: InsightSeries[] }>();
    for (const series of this.measured()) {
      const { point, telemetry } = series;
      const binding = point.binding;
      if (
        point.bindingState !== 'bound' ||
        binding?.source.kind !== 'device_channel' ||
        binding.source.deviceId === null ||
        (telemetry?.sources.length ?? 0) > 1
      ) {
        continue;
      }
      const key = `${binding.source.deviceId}:${binding.provenance}`;
      const group = groups.get(key) ?? { binding, members: [] };
      group.members.push(series);
      groups.set(key, group);
    }
    return groups;
  });

  readonly rows = computed<StripRow[]>(() => {
    const groups = this.deviceGroups();
    const grouped = new Set(Array.from(groups.values()).flatMap(group => group.members));
    const rows = this.series()
      .filter(series => !grouped.has(series))
      .map(({ point, telemetry }) =>
        point.nature === 'derived'
          ? this.derivedRow(point, telemetry)
          : this.measuredRow(point, telemetry),
      );
    const deviceRows = Array.from(groups, ([key, { binding, members }]) => ({
      key: `device:${key}`,
      provenance: binding.provenance,
      identity: binding.source.ldn ?? String(binding.source.deviceId),
      deviceLink: deviceLink(binding.source),
      lastDataAt: this.freshness(members),
      warning: null,
      sources: [],
    }));
    return [...deviceRows, ...rows];
  });

  private derivedRow(point: MeasurementPoint, telemetry: Point | null): StripRow {
    const source = telemetry?.sources[0]?.source;
    return {
      key: point.pointOid,
      provenance: telemetry?.sources[0]?.provenance ?? 'calculated',
      identity: source?.formula
        ? `${point.name} (${this.translate.instant(
            `installations.insights.strip.formula.${source.formula}`,
          )})`
        : point.name,
      deviceLink: null,
      lastDataAt: this.freshness([{ point, telemetry }]),
      warning: null,
      sources: [],
    };
  }

  private measuredRow(point: MeasurementPoint, telemetry: Point | null): StripRow {
    const binding = point.binding;
    return {
      key: point.pointOid,
      provenance: binding?.provenance ?? '',
      identity: binding ? `${point.name}: ${channelIdentity(binding.source)}` : point.name,
      deviceLink: deviceLink(binding?.source),
      lastDataAt: this.freshness([{ point, telemetry }]),
      warning: point.bindingState === 'bound' ? null : point.bindingState,
      sources: (telemetry?.sources.length ?? 0) > 1 ? (telemetry?.sources ?? []) : [],
    };
  }

  // Never older than the newest slot with data: a device whose readout is unknown or behind still
  // delivered that slot.
  private freshness(
    series: Pick<InsightSeries, 'point' | 'telemetry'>[],
  ): string | null | undefined {
    const newestSlot = lastDataAt(series.map(({ telemetry }) => telemetry));
    if (newestSlot === null) {
      return null;
    }
    const readings = this.latestReadings();
    if (readings === undefined) {
      return undefined;
    }
    return newestInstant([
      newestSlot,
      ...series.map(({ point }) => readings.get(point.pointOid) ?? null),
    ]);
  }

  provenanceIcon(provenance: string): string {
    return PROVENANCE_ICONS[provenance] ?? 'sensors';
  }

  sourceLink(source: Source): unknown[] | null {
    return deviceLink(source);
  }

  sourceIdentity(source: Source): string {
    return channelIdentity(source);
  }

  instant(time: string | null): string {
    return time ? zonedInstant(time, this.timezone(), 'D MMM YYYY HH:mm') : '';
  }

  relative(time: string | null): string {
    if (!time) {
      return this.translate.instant('installations.insights.strip.noData');
    }
    const minutes = Math.max(0, Math.round((Date.now() - new Date(time).getTime()) / 60_000));
    if (minutes < 1) return this.translate.instant('recentlyUsed.justNow');
    if (minutes < 60) return this.translate.instant('recentlyUsed.minutesAgo', { value: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return this.translate.instant('recentlyUsed.hoursAgo', { value: hours });
    return this.translate.instant('recentlyUsed.daysAgo', { value: Math.floor(hours / 24) });
  }

  toggle(row: StripRow): void {
    const next = new Set(this.expanded());
    if (next.has(row.key)) {
      next.delete(row.key);
    } else {
      next.add(row.key);
      this.appInsights.trackEvent('source_strip_expanded', { provenance: row.provenance });
    }
    this.expanded.set(next);
  }
}
